import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
} from '@buakei/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { assistantApi, feedbackApi, meApi } from '@/services/api';
import { saveFile } from '@/services/save-file';
import { Page } from '@/shell/Page';

/**
 * Your data (M6, ADR-031): download what Buakei AI holds about you, delete your chat history,
 * or ask for your account to be deleted. Each destructive step asks for confirmation.
 */
export function YourDataPage() {
  const tokens = useReaderTokens();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<'chats' | 'account' | null>(null);
  const download = useMutation({
    mutationFn: async () => {
      const data = await meApi.export(tokens!);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      await saveFile(blob, `buakei-ai-my-data-${data.exportedAt.slice(0, 10)}`, {
        extension: '.json',
        mime: 'application/json',
        description: 'JSON data',
      });
    },
  });
  const deleteChats = useMutation({
    mutationFn: () => assistantApi.deleteAllChats(tokens!),
    onSuccess: () => {
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'chats'] });
    },
  });
  const requestDeletion = useMutation({
    mutationFn: () =>
      feedbackApi.send(tokens!, {
        category: 'account_deletion',
        message: 'Please delete my Buakei AI account and the data held about me.',
        page: '/your-data',
      }),
    onSuccess: () => setConfirm(null),
  });

  if (!tokens) {
    return (
      <Page title="Your data">
        <EmptyState title="Verify your email address to manage your data" />
      </Page>
    );
  }
  return (
    <Page
      title="Your data"
      description="What Buakei AI holds about you, and how to download or delete it."
    >
      <Card>
        <CardHeader>
          <CardTitle as="h2">Download your data</CardTitle>
          <CardDescription>
            Your account details, your workspaces and roles, your Ask Buakei chats, and the research
            memos you wrote, as one JSON file.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button
            className="self-start"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending ? 'Preparing…' : 'Download my data'}
          </Button>
          {download.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {download.error.message}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h2">Delete your chat history</CardTitle>
          <CardDescription>
            Deletes every Ask Buakei chat you have. This cannot be undone. Memos you saved are not
            affected.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {deleteChats.isSuccess ? (
            <p role="status" className="text-sm text-ink-900">
              Your chat history was deleted.
            </p>
          ) : confirm === 'chats' ? (
            <div className="flex flex-wrap gap-2">
              <Button disabled={deleteChats.isPending} onClick={() => deleteChats.mutate()}>
                Confirm: delete all my chats
              </Button>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Keep them
              </Button>
            </div>
          ) : (
            <Button variant="secondary" className="self-start" onClick={() => setConfirm('chats')}>
              Delete my chat history
            </Button>
          )}
          {deleteChats.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {deleteChats.error.message}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h2">Delete your account</CardTitle>
          <CardDescription>
            Sends a request to the Buakei AI team, who delete your account and the data held about
            you within 30 days and confirm by email. Memos you shared with a firm workspace may be
            kept as the firm's records; the team will tell you which. Backups expire within 7 days.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {requestDeletion.isSuccess ? (
            <p role="status" className="text-sm text-ink-900">
              Your request was sent. The team will confirm by email.
            </p>
          ) : confirm === 'account' ? (
            <div className="flex flex-wrap gap-2">
              <Button disabled={requestDeletion.isPending} onClick={() => requestDeletion.mutate()}>
                Confirm: request account deletion
              </Button>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => setConfirm('account')}
            >
              Request account deletion
            </Button>
          )}
          {requestDeletion.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {requestDeletion.error.message}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </Page>
  );
}
